import { consoleLog } from "./helper";
import { REFRESH_TOKEN } from "./apiUrl";
import {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
  TOKEN_EXPIRED,
  TOKEN_INVALID,
  TOKEN_REVOKED,
  AUTHENTICATION_REQUIRED,
} from "./define";
import jwt_decode from "jwt-decode";

let isRefreshing = false;
let refreshPromise = null;

function FormGet(model) {
  let formGet = [];
  for (let property in model) {
    if (Array.isArray(model[property])) {
      model[property].forEach((element) => {
        let encodedKey = encodeURIComponent(property);
        let encodedValue = encodeURIComponent(element);
        formGet.push(encodedKey + "=" + encodedValue);
      });
    } else {
      let encodedKey = encodeURIComponent(property);
      let encodedValue = encodeURIComponent(model[property]);
      formGet.push(encodedKey + "=" + encodedValue);
    }
  }
  return formGet.join("&");
}

function Header(ContentType = "", header = null, token = null) {
  let headers = new Headers();
  if (header) {
    for (var element of header.entries()) {
      headers.append(element[0], element[1]);
    }
  }
  if (!token) token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    headers.append("Authorization", "bearer " + token);
  }
  if (ContentType.length > 0) {
    headers.append("Content-Type", ContentType);
  }
  return headers;
}

async function fetchWithTokenRefresh(url, options) {
  let response;
  try {
    response = await fetch(url, options);
  } catch (networkError) {
    consoleLog("Network error:", networkError);
    throw networkError;
  }

  if (response.status === 200) return response;
  if (response.status === 403) {
    alert("Bạn không có quyền truy cập chức năng này!");
    return response;
  }

  if (response.status === 401) {
    let errorData;
    try {
      errorData = await response.clone().json();
    } catch {
      return response;
    }
    const errorCode = errorData?.code;
    const unrecoverable = [TOKEN_REVOKED, TOKEN_INVALID, AUTHENTICATION_REQUIRED];
    if (unrecoverable.includes(errorCode)) {
      clearAuthAndRedirect();
      return response;
    }
    
    if (errorCode === TOKEN_EXPIRED) {
      
      // Lấy token từ request header để so sánh
      const requestToken = options.headers.get("Authorization")?.replace("bearer ", "");
      const currentToken = localStorage.getItem(TOKEN_KEY);
      // Nếu token trong localStorage ĐÃ KHÁC (nghĩa là đã có ai refresh rồi)
      // → Dùng token mới để retry, KHÔNG cần refresh lại
      if (currentToken && requestToken !== currentToken) {
        const newOptions = {
          ...options,
          headers: Header(
            options.headers.get("Content-Type") || "application/json",
            null,
            currentToken
          ),
        };
        return await fetch(url, newOptions);
      }
      
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshToken().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      } 
      
      try {
        const newToken = await refreshPromise;
        const newOptions = {
          ...options,
          headers: Header(
            options.headers.get("Content-Type") || "application/json",
            null,
            newToken
          ),
        };
        return await fetch(url, newOptions);
      } catch {
        console.error("❌ Refresh failed, redirecting...");
        clearAuthAndRedirect();
        return response;
      }
    }
  }
  return response;
}

export function clearAuthAndRedirect() {
  localStorage.clear();
  // window.location.href = "/login";
  window.location.href = "/";
}

export async function refreshToken() {
  try {
    const tokenValue = localStorage.getItem(TOKEN_KEY);
    const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);
    const response = await fetch(REFRESH_TOKEN, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accessToken: tokenValue,
        refreshToken: refreshTokenValue,
      }),
    });
    if (response.ok) {
      const data = await response.json();
      if (data.accessToken && data.refreshToken) {
        localStorage.setItem(TOKEN_KEY, data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(jwt_decode(data.accessToken)));
        return data.accessToken;
      }
    } else {
      clearAuthAndRedirect();
    }
  } catch (error) {
    clearAuthAndRedirect();
  }
}

// ====== Các phương thức API sử dụng fetchWithTokenRefresh ======
export async function Post(
  api,
  param = null,
  model,
  token = null,
  header = null,
  convertToJson = false
) {
  let API = api;
  if (param && param.toString().length > 0) {
    API = API + "/" + param;
  }
  const options = {
    method: "POST",
    headers: Header("application/json", header, token),
    body: JSON.stringify(model),
  };
  try {
    const res = await fetchWithTokenRefresh(API, options);
    if (!res) return null;
    return convertToJson ? res.json() : res;
  } catch (error) {
    consoleLog(error);
    return null;
  }
}

export async function Get(
  api,
  param = null,
  model = null,
  token = null,
  header = null,
  convertToJson = true
) {
  let API = api;
  if (param) {
    API = API + "/" + param;
  }
  if (model) {
    API = API + "?" + FormGet(model);
  }
  const options = {
    method: "GET",
    headers: Header("application/json", header, token),
  };
  try {
    const res = await fetchWithTokenRefresh(API, options);
    if (!res) return null;
    if (convertToJson) {
      if (res.ok) return res.json();
    } else {
      return res;
    }
  } catch (error) {
    consoleLog(error);
    return null;
  }
}

export async function DeleteSingle(api, param, token = null, header = null) {
  const options = {
    method: "DELETE",
    headers: Header("application/json", header, token),
  };
  try {
    const res = await fetchWithTokenRefresh(api + "/" + param, options);
    return res || null;
  } catch (error) {
    consoleLog(error);
    return error;
  }
}

export async function Put(
  api,
  param = null,
  model = null,
  token = null,
  header = null
) {
  let API = param && param.toString().length > 0 ? api + "/" + param : api;
  const options = {
    method: "PUT",
    headers: Header("application/json", header, token),
    body: JSON.stringify(model),
  };
  try {
    const res = await fetchWithTokenRefresh(API, options);
    return res || null;
  } catch (error) {
    consoleLog(error);
    return error;
  }
}

export async function UploadFile(
  api,
  param,
  data,
  token = null,
  header = null
) {
  let API = api;
  if (param) {
    API = API + "?" + FormGet(param);
  }
  const options = {
    method: "POST",
    body: data,
    headers: Header("", header, token),
  };
  try {
    const res = await fetchWithTokenRefresh(API, options);
    return res || null;
  } catch (error) {
    consoleLog(error);
    return error;
  }
}

export async function PostCheckStatus(api, param = null, model, convertToJson = false) {
  let API = api;
  if (param && param.toString().length > 0) {
    API = `${API}/${param}`;
  }
  return fetch(API, {
    method: 'POST',
    headers: Header('application/json'),
    body: JSON.stringify(model)
  })
    .then((res) => {
      if (convertToJson) {
        return res.json().then(data => {
          return {
            status: res.status,
            data
          };
        });
      }
      return res;
    })
    .catch((error) => {
      return {
        status: 500, // Giả sử lỗi kết nối hoặc lỗi không xác định
        data: null
      };
    });
}

